// The M10 juice table as the board FX read it (PURE): juice.json caps / events / particles / board, the
// palette colours the FX tint with, the easing functions resolved once (an unknown name fails at boot) and
// the overlay geometry settings. Numbers stay in juice.json / ui.json / palette.json; this is only a typed,
// pre-resolved view of them.
import type { JuiceData, PaletteData, UiData } from '../../data/schema.ts';
import { type EaseFn, easeFn, linear } from './ease.ts';
import type { GeoCfg } from './geometry.ts';
import type { ParticlePreset } from './particles.ts';

export type PresetName = keyof JuiceData['particles'];

export interface JuiceCfg {
  caps: JuiceData['caps'];
  aggregation: JuiceData['aggregation'];
  reduceMotion: JuiceData['reduceMotion'];
  lowQuality: JuiceData['lowQuality'];
  camera: JuiceData['camera'];
  ev: JuiceData['events'];
  board: JuiceData['board'];
  presets: Readonly<Record<PresetName, ParticlePreset>>;
  ease: { out: EaseFn; pop: EaseFn; inQuad: EaseFn; inOut: EaseFn; linear: EaseFn };
  /** Palette colours (0xrrggbb) the FX tint with. */
  color: {
    tier: readonly number[];
    material: Readonly<Record<string, number>>;
    glow: number;
    flash: number;
    rock: number;
    invalid: number;
    confetti: readonly number[];
  };
}

const hex = (h: string | undefined, fallback = 0xffffff): number => {
  if (h === undefined) return fallback;
  const n = Number.parseInt(h.replace('#', ''), 16);
  return Number.isFinite(n) ? n : fallback;
};

export function juiceCfg(j: JuiceData, palette: Readonly<Record<string, string>>): JuiceCfg {
  const tier: number[] = [];
  for (let t = 1; palette[`tier.${t}`] !== undefined; t++) tier.push(hex(palette[`tier.${t}`]));
  const material: Record<string, number> = {};
  for (const [k, v] of Object.entries(palette))
    if (k.startsWith('material.')) material[k.slice('material.'.length)] = hex(v);
  const confetti: number[] = [];
  for (let i = 1; palette[`confetti.${i}`] !== undefined; i++)
    confetti.push(hex(palette[`confetti.${i}`]));
  const o = j.easing.popOvershoot;
  return {
    caps: j.caps,
    aggregation: j.aggregation,
    reduceMotion: j.reduceMotion,
    lowQuality: j.lowQuality,
    camera: j.camera,
    ev: j.events,
    board: j.board,
    presets: j.particles,
    ease: {
      out: easeFn(j.easing.out, o),
      pop: easeFn(j.easing.pop, o),
      inQuad: easeFn(j.easing.in, o),
      inOut: easeFn(j.easing.inOut, o),
      linear,
    },
    color: {
      tier,
      material,
      glow: hex(palette['glow.merge']),
      flash: hex(palette['ui.cream']),
      rock: hex(palette['rock.light']),
      invalid: hex(palette['glow.invalid']),
      confetti,
    },
  };
}

/** The overlay geometry settings (ui.json sprites / render, juice.json board, palette.json art). */
export function geoCfg(
  j: Pick<JuiceData, 'board'>,
  ui: Pick<UiData, 'sprites' | 'render'>,
  art: Pick<PaletteData['art'], 'gunAngleDeg'>,
): GeoCfg {
  return {
    gunSize: ui.sprites.gunSize,
    gunLift: ui.sprites.gunLift,
    gunMountDeg: j.board.gunMountDeg,
    gunArtDeg: art.gunAngleDeg,
    arrowSize: ui.render.arrowSize,
    barWidth: ui.sprites.barWidth,
    barHeight: ui.render.barHeight,
    barLift: ui.sprites.barLift,
    targetSize: j.board.targetSize,
    iconSize: j.board.iconSize,
    digitH: j.board.digitH,
    pipSize: j.board.pipSize,
    heatBarW: j.board.heatBarW,
    heatBarH: j.board.heatBarH,
    countMax: j.board.countMax,
  };
}

/** The tier colour of tier `t` (1-based, clamped). */
export function tierColor(c: JuiceCfg, t: number): number {
  const i = Math.max(0, Math.min(c.color.tier.length - 1, t - 1));
  return c.color.tier[i] ?? 0xffffff;
}
